import React from "react"

import { AppModuleVideoSeries, SeasonResponse } from "@treefort/api-spec"

import {
  VideoSeriesModuleBanner,
  VideoSeriesModuleEpisode,
  VideoSeriesModuleHorizontalEpisodesList,
  getVideoSeriesModuleEpisodeDimensions,
  getVideoSeriesModuleBannerDimensions,
  VideoSeriesModuleSeasonPillGroup,
  getVideoSeriesModuleSeasonPillGroupDimensions,
  VideoSeriesModuleDescription,
  getVideoSeriesModuleDescriptionDimensions,
} from "../../../../components/modules/video-series"
import { ResolvedTokens } from "../../../../components/tokens-provider"
import { ListViewModule, ItemArgs, AllItemsArgs } from "../list-view-module"

type Item =
  | { type: "banner" }
  | { type: "description" }
  | { type: "seasonPillGroup" }
  | { type: "horizontalEpisodesList" }
  | { type: "episode"; episode: SeasonResponse["episodes"][number] }
  | { type: "gap"; size: number }

type Props = Pick<
  Parameters<typeof VideoSeriesModuleBanner>[0],
  "videoSeries" | "currentEpisode" | "isSeriesStarted"
> &
  Pick<
    Parameters<typeof VideoSeriesModuleSeasonPillGroup>[0],
    "seasonIndex" | "onChangeSeasonIndex"
  >

export type VideoSeriesLayout = "horizontal" | "verticalWide" | "verticalNarrow"

export class VideoSeriesListViewModule
  implements ListViewModule<AppModuleVideoSeries, Item>
{
  readonly appModule: AppModuleVideoSeries
  readonly appHeaderPresentation = "transparent"
  readonly appHeaderState = undefined
  readonly gapSize = 36
  readonly maxWidth = undefined

  readonly props: Props

  private getLayout = (args: { tokens: ResolvedTokens }): VideoSeriesLayout => {
    if (args.tokens.videoSeriesModule.forceVerticalNarrowLayout) {
      return "verticalNarrow"
    } else {
      return this.appModule.config.preferredLayout === "horizontal"
        ? "horizontal"
        : "verticalWide"
    }
  }

  private getHorizontalPadding = (args: ItemArgs<Item>) => {
    const layout = this.getLayout(args)

    return layout === "verticalWide"
      ? {
          paddingLeft:
            args.displayWidth <= args.tokens.breakpoints.desktop
              ? args.tokens.spacing.xlarge
              : args.tokens.spacing.yuge,
          paddingRight: args.tokens.spacing.xlarge,
        }
      : {
          paddingLeft: args.tokens.spacing.pagePaddingHorizontal,
          paddingRight: args.tokens.spacing.pagePaddingHorizontal,
        }
  }

  constructor(appModule: AppModuleVideoSeries, props: Props) {
    this.appModule = appModule
    this.props = props
  }

  getItems = (args: AllItemsArgs) => {
    return [
      { type: "banner" as const },
      ...(this.props.videoSeries.description
        ? [{ type: "description" as const }]
        : []),
      ...(this.props.videoSeries.details.seasons.some((season) =>
        Boolean(season.title),
      )
        ? [{ type: "seasonPillGroup" as const }]
        : []),
      ...(this.getLayout(args) === "horizontal"
        ? [
            {
              type: "gap" as const,
              size: args.tokens.videoSeriesModule.episodeListPaddingTop,
            },
            { type: "horizontalEpisodesList" as const },
          ]
        : this.props.videoSeries.details.seasons[
            this.props.seasonIndex
          ].episodes.flatMap((episode, index) => [
            {
              type: "gap" as const,
              size:
                index === 0
                  ? args.tokens.videoSeriesModule.episodeListPaddingTop
                  : args.tokens.spacing.xlarge,
            },
            { type: "episode" as const, episode },
          ])),
    ]
  }

  renderItem = (args: ItemArgs<Item>) => {
    const layout = this.getLayout(args)
    const horizontalPadding = this.getHorizontalPadding(args)

    switch (args.item.type) {
      case "banner":
        return (
          <VideoSeriesModuleBanner
            {...this.appModule}
            {...this.props}
            {...horizontalPadding}
            paddingTop={args.safeAreaInsets.top}
            layout={layout}
          />
        )
      case "description":
        return (
          <VideoSeriesModuleDescription
            {...this.appModule}
            {...this.props}
            {...horizontalPadding}
            layout={layout}
          />
        )
      case "seasonPillGroup":
        return (
          <VideoSeriesModuleSeasonPillGroup
            videoSeries={this.props.videoSeries}
            onChangeSeasonIndex={this.props.onChangeSeasonIndex}
            seasonIndex={this.props.seasonIndex}
            {...horizontalPadding}
          />
        )
      case "horizontalEpisodesList":
        return (
          <VideoSeriesModuleHorizontalEpisodesList
            {...horizontalPadding}
            dateDisplayStrategy={this.props.videoSeries.dateDisplayStrategy}
            episodes={
              this.props.videoSeries.details.seasons[this.props.seasonIndex]
                .episodes
            }
          />
        )
      case "episode":
        return (
          <VideoSeriesModuleEpisode
            {...horizontalPadding}
            dateDisplayStrategy={this.props.videoSeries.dateDisplayStrategy}
            episode={args.item.episode}
            layout={layout}
          />
        )
      default:
        return <></>
    }
  }

  getItemKey = ({ pageKey, indexInPage }: ItemArgs<Item>) =>
    pageKey + indexInPage + this.appModule.id

  getItemSize = (args: ItemArgs<Item>) => {
    const layout = this.getLayout(args)
    const padding = this.getHorizontalPadding(args)

    switch (args.item.type) {
      case "gap":
        return args.item.size
      case "description":
        return getVideoSeriesModuleDescriptionDimensions().height
      case "seasonPillGroup":
        return getVideoSeriesModuleSeasonPillGroupDimensions(args).height
      case "horizontalEpisodesList":
        return getVideoSeriesModuleEpisodeDimensions({
          ...padding,
          tokens: args.tokens,
          layout,
          windowDimensions: {
            width: args.displayWidth,
            height: args.displayHeight,
          },
          hasDescription: this.props.videoSeries.details.seasons[
            this.props.seasonIndex
          ].episodes.some((episode) => episode.description !== undefined),
        }).height
      case "banner":
        return getVideoSeriesModuleBannerDimensions({
          tokens: args.tokens,
          displayHeight: args.displayHeight,
          paddingTop: args.safeAreaInsets.top,
        }).height
      case "episode": {
        return getVideoSeriesModuleEpisodeDimensions({
          ...padding,
          tokens: args.tokens,
          layout,
          windowDimensions: {
            width: args.displayWidth,
            height: args.displayHeight,
          },
          hasDescription: args.item.episode.description !== undefined,
        }).height
      }
    }
  }
}
